import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface LocalUploadState {
  progress: number;
  error: string | null;
  needsFile: boolean;
}

const EMPTY_UPLOAD_STATE: LocalUploadState = {
  progress: 0,
  error: null,
  needsFile: false,
};

interface AssistantState {
  isOpen: boolean;
  activeConversationId: string | null;
  isHideAssistantBanner: boolean;
  localUploads: Record<string, LocalUploadState>;
  setOpen: (isOpen: boolean) => void;
  toggle: () => void;
  setActiveConversation: (id: string | null) => void;
  setHideAssistantBanner: (isHide: boolean) => void;
  setUploadState: (id: string, patch: Partial<LocalUploadState>) => void;
  clearUploadState: (id: string) => void;
}

function storageKey(name: string) {
  if (!import.meta.env.DEV) return name;
  const tenant = localStorage.getItem('xt:dev-tenant');
  return tenant ? `${name}:${tenant}` : name;
}

export const useAssistantStore = create<AssistantState>()(
  persist(
    (set) => ({
      isOpen: false,
      activeConversationId: null,
      isHideAssistantBanner: false,
      localUploads: {},
      setOpen: (isOpen) => set({ isOpen }),
      toggle: () => set((state) => ({ isOpen: !state.isOpen })),
      setActiveConversation: (activeConversationId) =>
        set({ activeConversationId }),
      setHideAssistantBanner: (isHideAssistantBanner) =>
        set({ isHideAssistantBanner }),
      setUploadState: (id, patch) =>
        set((state) => ({
          localUploads: {
            ...state.localUploads,
            [id]: {
              ...(state.localUploads[id] ?? EMPTY_UPLOAD_STATE),
              ...patch,
            },
          },
        })),
      clearUploadState: (id) =>
        set((state) => {
          const { [id]: _removed, ...rest } = state.localUploads;
          return { localUploads: rest };
        }),
    }),
    {
      name: 'xtreed-assistant',
      storage: createJSONStorage(() => ({
        getItem: (name) => {
          try {
            return localStorage.getItem(storageKey(name));
          } catch {
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            localStorage.setItem(storageKey(name), value);
          } catch {
            return;
          }
        },
        removeItem: (name) => {
          try {
            localStorage.removeItem(storageKey(name));
          } catch {
            return;
          }
        },
      })),
      partialize: (state) => ({
        isOpen: state.isOpen,
        activeConversationId: state.activeConversationId,
        isHideAssistantBanner: state.isHideAssistantBanner,
      }),
    },
  ),
);
